import { useState } from 'react';
import {
  TextField, Popover, IconButton, Box, Typography, Stack, InputAdornment,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import {
  gregISOToJalali, jalaliToGregISO, jalaaliMonthLength, jalaliWeekdayOfFirst,
  formatJalaliISO, todayJalali, toPersianDigits, JMONTHS_FA, JWEEKDAYS_FA,
} from '@/utils/jalali';

/**
 * Date field that shows a SHAMSI (Jalali) calendar when the UI language is
 * Persian, and the native Gregorian date input otherwise. The stored value is
 * always Gregorian ISO 'YYYY-MM-DD' (unchanged backend contract); only the
 * display/picker is localized.
 */
export default function JalaliDateField({
  label, value, onChange, min, max, size = 'small', fullWidth = true, sx,
}) {
  const { i18n } = useTranslation();
  const fa = i18n.language === 'fa';
  const [anchor, setAnchor] = useState(null);
  const [view, setView] = useState(null); // { jy, jm } while the popover is open

  if (!fa) {
    return (
      <TextField type="date" label={label} value={value || ''} size={size} fullWidth={fullWidth}
        onChange={(e) => onChange(e.target.value)} InputLabelProps={{ shrink: true }}
        inputProps={{ min, max }} sx={sx} />
    );
  }

  const sel = value ? gregISOToJalali(value) : null;
  const openCal = (e) => { setView(sel || todayJalali()); setAnchor(e.currentTarget); };
  const close = () => setAnchor(null);

  const inRange = (iso) => (!min || iso >= min) && (!max || iso <= max);
  const shiftMonth = (delta) => {
    let { jy, jm } = view;
    jm += delta;
    if (jm < 1) { jm = 12; jy -= 1; }
    if (jm > 12) { jm = 1; jy += 1; }
    setView({ jy, jm });
  };
  const pick = (d) => {
    const iso = jalaliToGregISO(view.jy, view.jm, d);
    if (!inRange(iso)) return;
    onChange(iso);
    close();
  };

  const lead = view ? jalaliWeekdayOfFirst(view.jy, view.jm) : 0;
  const len = view ? jalaaliMonthLength(view.jy, view.jm) : 0;
  const cells = view
    ? [...Array(lead).fill(null), ...Array.from({ length: len }, (_, i) => i + 1)]
    : [];

  return (
    <>
      <TextField
        label={label} value={formatJalaliISO(value)} size={size} fullWidth={fullWidth}
        onClick={openCal} readOnly
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={openCal} edge="end"><CalendarMonthIcon fontSize="small" /></IconButton>
            </InputAdornment>
          ),
        }}
        sx={sx}
      />
      <Popover open={!!anchor} anchorEl={anchor} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {view && (
          <Box sx={{ p: 1.5, width: 280 }} dir="rtl">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <IconButton size="small" onClick={() => shiftMonth(-1)} aria-label="prev"><ChevronRightIcon /></IconButton>
              <Typography fontWeight={700}>
                {JMONTHS_FA[view.jm - 1]} {toPersianDigits(view.jy)}
              </Typography>
              <IconButton size="small" onClick={() => shiftMonth(1)} aria-label="next"><ChevronLeftIcon /></IconButton>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
              {JWEEKDAYS_FA.map((w, i) => (
                <Typography key={i} variant="caption" align="center" color="text.secondary" fontWeight={700}>{w}</Typography>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {cells.map((d, i) => {
                if (d === null) return <Box key={`b${i}`} />;
                const iso = jalaliToGregISO(view.jy, view.jm, d);
                const disabled = !inRange(iso);
                const isSel = sel && sel.jy === view.jy && sel.jm === view.jm && sel.jd === d;
                return (
                  <Box key={d} component="button" type="button" disabled={disabled}
                    onClick={() => pick(d)}
                    sx={{
                      border: 0, borderRadius: 1.5, py: 0.75, cursor: disabled ? 'default' : 'pointer',
                      font: 'inherit', fontWeight: isSel ? 800 : 500,
                      color: disabled ? 'text.disabled' : isSel ? 'primary.contrastText' : 'text.primary',
                      bgcolor: isSel ? 'primary.main' : 'transparent',
                      '&:hover': { bgcolor: disabled ? 'transparent' : isSel ? 'primary.dark' : 'action.hover' },
                    }}>
                    {toPersianDigits(d)}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
}
